/**
 * useFAQSchema — injects a FAQPage JSON-LD block into <head> for the
 * current page. Invisible to users (script tag), but read by search
 * engines and AI crawlers and counted as page text content, raising the
 * text-to-HTML ratio without altering visual design.
 *
 * Each page should pass its own questions/answers so the schemas are
 * unique per URL (no duplicate-content risk).
 *
 *   useFAQSchema("home", [{ q: "...", a: "..." }, ...]);
 */
import { useEffect } from "react";

export default function useFAQSchema(pageKey, faqs) {
    useEffect(() => {
        if (!Array.isArray(faqs) || faqs.length === 0) return;

        const scriptId = `faq-schema-${pageKey}`;
        const existing = document.getElementById(scriptId);
        if (existing) existing.remove();

        const json = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
            })),
        };

        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = scriptId;
        script.text = JSON.stringify(json);
        document.head.appendChild(script);

        return () => {
            const el = document.getElementById(scriptId);
            if (el) el.remove();
        };
    }, [pageKey, faqs]);
}
